/*
 * Carcaça de trilho DIN compartilhada por BM-2, SDB e SDV-V2 — mesmo corpo,
 * moldura, painel frontal e parafusos nos 3 (ver
 * docs/specs/cma-modulos-auxiliares-3d.md §5.1/§5.2/§5.3). `bm2.js` é o
 * primeiro a consumir isto; SDB e SDV-V2 reaproveitam sem duplicar.
 */
import * as THREE from 'three';
import { box, roundedRect } from '../geometriaHelpers';

// Corpo "puro", sem os blocos de terminais nem o clipe de trilho — esses se
// projetam além dele e entram no builder de cada módulo (ver §5.1, passos
// 1 e 5). Medidas estimadas por proporção visual, não por desenho cotado
// (ver §1.1 da spec) — ajustar aqui quando houver cota real.
export const DIN_BODY_WIDTH = 0.20;
export const DIN_BODY_HEIGHT = 0.15;
export const DIN_BODY_DEPTH = 0.16;

const FRAME_MARGIN = 0.012; // moldura maior que o corpo, em X/Y, por lado
const FRAME_PROTRUSION = 0.010; // quanto a moldura sai à frente do corpo
const FRAME_CORNER_RATIO = 0.09;

const PANEL_INSET = 0.016; // painel cinza recuado da moldura, em X/Y
const PANEL_THICKNESS = 0.006;
const PANEL_RECESS = 0.003; // painel um pouco atrás da face da moldura

const SCREW_RADIUS = 0.0035;
const SCREW_HEIGHT = 0.006;
const SCREW_MARGIN = 0.018; // distância do centro do parafuso à borda da moldura

/**
 * Monta corpo + moldura + painel frontal + 4 parafusos. Devolve o grupo e
 * os planos/dimensões que cada módulo usa para encaixar seus próprios
 * decalques, LEDs e blocos de terminais (`panelZ` é a face da frente do
 * painel, em Z local; `topY`/`bottomY` as faces de cima/baixo do corpo).
 */
export function buildCorpoDin(materiais, {
  width = DIN_BODY_WIDTH,
  height = DIN_BODY_HEIGHT,
  depth = DIN_BODY_DEPTH,
} = {}) {
  const group = new THREE.Group();

  const corpo = box(width, height, depth, materiais.corpo, 0, 0, 0);
  group.add(corpo);

  const frameW = width + FRAME_MARGIN * 2;
  const frameH = height + FRAME_MARGIN * 2;
  const bodyFrontZ = depth / 2;
  const frameCenterZ = bodyFrontZ + FRAME_PROTRUSION / 2;
  const frameFrontZ = bodyFrontZ + FRAME_PROTRUSION;

  const moldura = roundedRect(
    frameW, frameH, Math.min(frameW, frameH) * FRAME_CORNER_RATIO, FRAME_PROTRUSION,
    materiais.moldura, 0, 0, frameCenterZ,
  );
  group.add(moldura);

  const panelW = width - PANEL_INSET * 2;
  const panelH = height - PANEL_INSET * 2;
  const panelFrontZ = frameFrontZ - PANEL_RECESS;
  const panelCenterZ = panelFrontZ - PANEL_THICKNESS / 2;
  const painel = box(panelW, panelH, PANEL_THICKNESS, materiais.painel, 0, 0, panelCenterZ);
  group.add(painel);

  const screwZ = frameFrontZ - SCREW_HEIGHT / 2;
  [
    [-frameW / 2 + SCREW_MARGIN, frameH / 2 - SCREW_MARGIN],
    [frameW / 2 - SCREW_MARGIN, frameH / 2 - SCREW_MARGIN],
    [-frameW / 2 + SCREW_MARGIN, -frameH / 2 + SCREW_MARGIN],
    [frameW / 2 - SCREW_MARGIN, -frameH / 2 + SCREW_MARGIN],
  ].forEach(([sx, sy]) => {
    const parafuso = new THREE.Mesh(
      new THREE.CylinderGeometry(SCREW_RADIUS, SCREW_RADIUS, SCREW_HEIGHT, 12),
      materiais.parafuso,
    );
    parafuso.rotation.x = Math.PI / 2;
    parafuso.position.set(sx, sy, screwZ);
    group.add(parafuso);
  });

  return {
    group,
    width,
    height,
    depth,
    frameWidth: frameW,
    frameHeight: frameH,
    panelWidth: panelW,
    panelHeight: panelH,
    panelFrontZ,
    topY: height / 2,
    bottomY: -height / 2,
    backZ: -depth / 2,
  };
}
